// assets/product-photo-upload.js
import { Component } from '@theme/component';

const PRINTING_OPTION_NAME = 'Printing';
const WITH_PRINTING_PREFIX = 'with printing';

/**
 * @typedef {object} ProductPhotoUploadRefs
 * @property {HTMLInputElement} input - The file input.
 */

/**
 * Attach-a-photo block.
 *
 * When the product has a "Printing" variant option, the photo field is only
 * usable — and required to add to cart — while a "With Printing" value is
 * selected. It's hidden, disabled, and cleared while "No Printing" is
 * selected. Products without a "Printing" option are unaffected; the block's
 * own "required" setting applies as usual.
 *
 * Shopify's "Buy it now" button can't upload files — it only sends the file
 * input's text value ("C:\fakepath\..."). So when a photo is attached, the
 * click is intercepted: the form is posted to /cart/add.js (which uploads the
 * file, same as Add to cart) and the customer is sent straight to checkout.
 *
 * @extends Component<ProductPhotoUploadRefs>
 */
class ProductPhotoUpload extends Component {
  requiredRefs = ['input'];

  /** @type {Element | null} */
  #picker = null;

  connectedCallback() {
    super.connectedCallback();

    const sectionId = this.querySelector('[data-section-id]')?.getAttribute('data-section-id');
    const picker = sectionId ? document.querySelector(`variant-picker[data-section-id="${sectionId}"]`) : null;
    if (!picker || !picker.querySelector(`[data-option-name="${PRINTING_OPTION_NAME}"]`)) return;

    this.#picker = picker;
    picker.addEventListener('change', this.#onPrintingChange);
    this.#applyPrintingValue(this.#selectedPrintingValue());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#picker?.removeEventListener('change', this.#onPrintingChange);
  }

  /** @param {Event} event */
  #onPrintingChange = (event) => {
    const target = event.target;
    const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!(selectedOption instanceof HTMLElement) || selectedOption.dataset.optionName !== PRINTING_OPTION_NAME) return;
    this.#applyPrintingValue(this.#valueOf(selectedOption));
  };

  #selectedPrintingValue() {
    const options = this.#picker?.querySelectorAll(`[data-option-name="${PRINTING_OPTION_NAME}"]`) ?? [];
    for (const option of options) {
      if (option instanceof HTMLInputElement && option.checked) return this.#valueOf(option);
      if (option instanceof HTMLOptionElement && option.selected) return this.#valueOf(option);
    }
    return '';
  }

  /** @param {HTMLElement} element */
  #valueOf(element) {
    return element instanceof HTMLInputElement || element instanceof HTMLOptionElement ? element.value : '';
  }

  /** @param {string} value */
  #applyPrintingValue(value) {
    const { input } = this.refs;
    const withPrinting = value.trim().toLowerCase().startsWith(WITH_PRINTING_PREFIX);

    this.hidden = !withPrinting;
    input.disabled = !withPrinting;
    input.required = withPrinting;
    input.toggleAttribute('aria-required', withPrinting);
    if (!withPrinting) input.value = '';
  }
}

customElements.define('product-photo-upload-component', ProductPhotoUpload);

let buyingNow = false;

/**
 * Finds the photo input that belongs to a product form: the closest one found
 * walking up from the form, without leaving its section or popup. The input sits
 * outside the form (linked by the `form` attribute), and the product page and a
 * quick-add popup can hold forms with the same id, so `form.elements` isn't reliable.
 * @param {HTMLFormElement} form
 */
function findPhotoScope(form) {
  for (let scope = form.parentElement; scope; scope = scope.parentElement) {
    const input = scope.querySelector('product-photo-upload-component input[type="file"]');
    if (input instanceof HTMLInputElement) return { scope, input };
    if (scope.matches('dialog, .shopify-section')) break;
  }
  return null;
}

/** @param {MouseEvent} event */
async function onBuyNowClick(event) {
  const host = event
    .composedPath()
    .find((el) => el instanceof Element && el.matches('.shopify-payment-button, shopify-accelerated-checkout'));
  const form = host instanceof Element ? host.closest('form') : null;
  const found = form ? findPhotoScope(form) : null;
  if (!form || !found || found.input.disabled) return;

  const { scope, input } = found;
  const file = input.files?.[0];
  if (!file && !input.required) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (!file) {
    input.reportValidity();
    return;
  }
  if (buyingNow) return;

  const body = new FormData(form);
  const formId = form.getAttribute('id');
  const fields = formId ? scope.querySelectorAll(`[form="${CSS.escape(formId)}"][name]`) : [];
  for (const field of fields) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) || field.disabled) continue;
    if (field instanceof HTMLInputElement && field.type === 'file') {
      if (field.files?.[0]) body.set(field.name, field.files[0]);
    } else {
      body.set(field.name, field.value);
    }
  }

  buyingNow = true;
  const container = form.querySelector('.shopify-payment-button');
  if (container instanceof HTMLElement) container.style.cssText += 'opacity:0.6;pointer-events:none;';

  const root = window.Shopify?.routes?.root ?? '/';
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'Could not add this item.');
    }
    window.location.href = `${root}checkout`;
  } catch (error) {
    buyingNow = false;
    if (container instanceof HTMLElement) container.style.opacity = container.style.pointerEvents = '';
    window.alert(error instanceof Error ? error.message : String(error));
  }
}

document.addEventListener('click', onBuyNowClick, true);
